import Header from '@amzn/awsui-components-react/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { EventEngineEventsService } from '@amzn/event-engine-events-sdk';
import {
    lazyPreviewComponentImport,
    LogLevel,
    useLogger,
} from '@amzn/event-engine-js-utils';
import { state as breadcrumbsState } from 'atoms/breadcrumbs';
import { state as contentTypeState } from 'atoms/content-type';
import { state as maxContentWidthState } from 'atoms/max-content-width';
import { state as navigationContentState } from 'atoms/navigation-content';
import { state as toolsState } from 'atoms/tools';
import ErrorBoundaryWithLogger from 'components/ErrorBoundaryWithLogger';
import SpinnerWithLabel from 'components/SpinnerWithLabel';
import { useEventParticipant } from 'hooks';
import React, {
    ComponentProps,
    FC,
    lazy,
    ReactNode,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { useSetRecoilState } from 'recoil';
import EEEvents from 'services/ee-events';
import { i18nStrings } from 'utils/constants';

import { routes } from '../../constants';
import EventDashboardHelp from './components/EventDashboardHelp';
import EventInformationContainer from './components/EventInformationContainer';
import EventOutputsTable from './components/EventOutputsTable';
import TeamCredentialsLink from './components/TeamCredentialsLink';
import TeamLoginLink from './components/TeamLoginLink';
import TeamSshKeyLink from './components/TeamSshKeyLink';
import WorkshopInformationContainer from './components/WorkshopInformationContainer';
import { BASE_BREADCRUMBS, WORKSHOP_PATH } from './constants';

const LazyPreviewComponent = lazy(lazyPreviewComponentImport);
type PreviewComponentProps = ComponentProps<typeof LazyPreviewComponent>;

export interface EventDashboardProps {
    eventData: EventEngineEventsService.ParticipantGetEventResponse;
    participantId: string;
}

const EventDashboard: FC<EventDashboardProps> = ({
    eventData,
    participantId,
}) => {
    const { clearParticipant } = useEventParticipant();
    const [logger] = useLogger(
        'EventDashboardLogger',
        process.env.NODE_ENV === 'development'
            ? LogLevel.VERBOSE
            : LogLevel.ERROR
    );
    const setNavigationContent = useSetRecoilState(navigationContentState);
    const setTools = useSetRecoilState(toolsState);
    const getContentRequest = useCallback(
        () => EEEvents.getParticipantContent(participantId),
        []
    );
    const navigationFooterItems = useMemo<
        PreviewComponentProps['footerItems'] | undefined
    >(() => {
        const items: { content: ReactNode }[] = [];

        eventData.accountSource === 'workshop_studio' &&
            items.push(
                {
                    content: (
                        <TeamLoginLink
                            participantId={participantId}
                            deploymentRegions={eventData.deploymentRegions}
                        />
                    ),
                },
                {
                    content: (
                        <TeamCredentialsLink
                            participantId={participantId}
                            accessibleRegions={eventData.accessibleRegions}
                            deploymentRegions={eventData.deploymentRegions}
                        />
                    ),
                }
            );

        eventData.accountSummary?.ec2KeyPair &&
            items.push({
                content: <TeamSshKeyLink participantId={participantId} />,
            });

        return items.length
            ? [
                  {
                      type: 'expandable',
                      text: i18nStrings.event.sections.AWSAccountAccess,
                      items,
                  },
              ]
            : undefined;
    }, []);
    const eventConfig = {
        title: eventData.title,
        pages: [
            {
                path: '/',
                content: (
                    <SpaceBetween direction="vertical" size="m">
                        <Header variant="h1">{eventData.title}</Header>
                        <SpaceBetween direction="vertical" size="l">
                            <EventInformationContainer eventData={eventData} />
                            <WorkshopInformationContainer
                                contentSummary={eventData.contentSummary}
                                isContentVisible
                            />
                            <EventOutputsTable participantId={participantId} />
                        </SpaceBetween>
                    </SpaceBetween>
                ),
            },
        ],
        baseBreadcrumbs: BASE_BREADCRUMBS,
        workshopPath: WORKSHOP_PATH,
    };
    const exitNavigation: PreviewComponentProps['exitNavigation'] = {
        type: 'link',
        text: i18nStrings.event.exit,
        href: routes.landingPage,
    };

    useEffect(() => {
        setTools({
            open: false,
            hide: false,
            content: <EventDashboardHelp />,
        });

        return () => {
            setTools({
                open: false,
                hide: true,
                content: null,
            });
            setNavigationContent(null);
        };
    }, []);

    return (
        <ErrorBoundaryWithLogger loggerName="EventErrorBoundaryLogger">
            <Suspense
                fallback={
                    <SpinnerWithLabel>
                        {i18nStrings.event.loadingContent}
                    </SpinnerWithLabel>
                }>
                <LazyPreviewComponent
                    logger={logger}
                    contentBuildId={eventData.contentBuildId}
                    getContentArtifact={getContentRequest}
                    defaultAppNav={null}
                    navigationState={navigationContentState}
                    breadcrumbsState={breadcrumbsState}
                    maxContentWidthState={maxContentWidthState}
                    contentTypeState={contentTypeState}
                    eventConfig={eventConfig}
                    footerItems={navigationFooterItems}
                    exitNavigation={exitNavigation}
                    onExitNavigation={clearParticipant}
                    defaultAppTitleName={document.title}
                />
            </Suspense>
        </ErrorBoundaryWithLogger>
    );
};

export default EventDashboard;
